import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BoostSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');
  const postId = searchParams.get('post_id');

  useEffect(() => {
    if (!sessionId) {
      setError('Missing payment session information');
      return;
    }

    checkPaymentStatus();
  }, [sessionId]);

  const checkPaymentStatus = async (attempt = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempt >= maxAttempts) {
      setError('Payment verification timed out. Please check your email for confirmation or contact support.');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/payments/status/${sessionId}`, { headers });
      const data = response.data;
      
      if (data.is_successful) {
        setPaymentStatus('success');
        setPaymentData(data);
        return;
      } else if (data.is_expired) {
        setPaymentStatus('expired');
        setPaymentData(data);
        return;
      }

      // If payment is still pending, continue polling
      setPaymentStatus('processing');
      setTimeout(() => checkPaymentStatus(attempt + 1), pollInterval);
      
    } catch (error) {
      console.error('Error checking payment status:', error);
      setError('Error verifying payment. Please contact support if you were charged.');
      setPaymentStatus('error');
    }
  };

  const renderContent = () => {
    switch (paymentStatus) {
      case 'checking':
      case 'processing':
        return (
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {paymentStatus === 'checking' ? 'Verifying Payment...' : 'Processing Payment...'}
            </h2>
            <p className="text-gray-600 mb-4">
              Please wait while we confirm your payment and activate your post boost.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                🔒 <strong>Secure Payment Processing</strong><br />
                Your payment is being processed securely through Stripe. Do not close this window.
              </p>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-green-800 mb-4">🎉 Boost Activated!</h2>
            <p className="text-lg text-gray-700 mb-6">
              Your post has been successfully boosted and is now featured in the main feed!
            </p>
            
            {paymentData && (
              <div className="bg-green-50 p-6 rounded-lg border border-green-200 mb-6">
                <h3 className="font-semibold text-green-800 mb-3">Boost Details:</h3>
                <div className="space-y-2 text-sm text-green-700">
                  <p><strong>Package:</strong> {paymentData.metadata.package_name || 'Boost Package'}</p>
                  <p><strong>Amount:</strong> ${paymentData.amount_total.toFixed(2)}</p>
                  <p><strong>Duration:</strong> {paymentData.metadata.duration_hours || 24} hours</p>
                  <p><strong>Status:</strong> Active</p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow"
              >
                🌟 View Boosted Post on Dashboard
              </button>
              <div className="text-center">
                <button
                  onClick={() => navigate('/my-life')}
                  className="text-gray-600 hover:text-gray-800 underline"
                >
                  Or view in My Life →
                </button>
              </div>
            </div>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-yellow-800 mb-4">Payment Session Expired</h2>
            <p className="text-gray-700 mb-6">
              Your payment session has expired. Don't worry - your post was created successfully, but the boost was not applied.
            </p>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-6">
              <p className="text-sm text-yellow-800">
                💡 <strong>No charge was made to your card.</strong><br />
                You can boost your post anytime from the dashboard.
              </p>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        );

      case 'error':
      default:
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-red-800 mb-4">Payment Verification Error</h2>
            <p className="text-gray-700 mb-6">
              {error || 'There was an issue verifying your payment. Please contact support if you were charged.'}
            </p>
            
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 mb-6">
              <p className="text-sm text-red-800">
                🆘 <strong>Need Help?</strong><br />
                Please contact support with your session ID: <code className="bg-white px-1 rounded">{sessionId}</code>
              </p>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6 text-white text-center">
            <h1 className="text-2xl font-bold">💳 Payment Processing</h1>
            <p className="text-emerald-100 mt-2">Modern Human Being Platform</p>
          </div>

          {/* Content */}
          <div className="p-8">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoostSuccess;